import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, TrendingDown, TrendingUp } from "lucide-react";

import DrilldownSheet from "../DrilldownSheet";
import ReportChart from "../ReportChart";
import ReportTable from "../ReportTable";
import { colorFor } from "./palette";
import HowItWorks from "./HowItWorks";
import ReportControls from "./ReportControls";
import { fetchMonthDetails, fetchReport } from "../../lib/api";

/**
 * Monthly recurring revenue, inside the pack.
 *
 * The same report as the Reporting page's MRR: the same endpoint, the same
 * chart, the same table and the same month drilldown. Only the filters differ,
 * and they are the report definition's own, set in the editor, so the pack's
 * MRR is configured like any other board report rather than fixed in code.
 *
 * Revenue is not recomputed here. Month attribution, overlapping renewal terms
 * and live RPI stay where they were proven.
 */

const pad = (n) => String(n).padStart(2, "0");

/** The fiscal year the pack is showing, as the MRR endpoint's month window. */
export function fiscalYearWindow(fyStartYear, fyStartMonth = 7) {
  const endMonth = fyStartMonth === 1 ? 12 : fyStartMonth - 1;
  const endYear = fyStartMonth === 1 ? fyStartYear : fyStartYear + 1;
  return { from: `${fyStartYear}-${pad(fyStartMonth)}`, to: `${endYear}-${pad(endMonth)}` };
}

/** The fiscal years a comparison can be drawn against: 2024/25 onwards, up to
 * the year before the one on screen. Earlier years are not in HubSpot in a
 * shape worth comparing. */
export const FIRST_COMPARISON_FY = 2024;

export function comparisonYears(fyStartYear) {
  const years = [];
  for (let year = FIRST_COMPARISON_FY; year < Number(fyStartYear || 0); year += 1) {
    years.push({ value: year, label: `${year}/${String(year + 1).slice(2)}` });
  }
  return years.reverse();
}

/** Last year's months lined up with this year's, month one against month one. */
export function comparisonByIndex(months = []) {
  return months.map((month) => Number(month.total_mrr || 0));
}

/**
 * The pack's filters for the MRR endpoint.
 *
 * The board's team narrows the report's own pipelines rather than replacing
 * them: a report restricted to one pipeline, read as the other team, is empty,
 * which is the honest answer.
 */
export function revenueFilters(config = {}, teamPipelines = null, window = {}) {
  const own = config.pipelines || [];
  let pipelines = own;
  if (teamPipelines && teamPipelines.length) {
    pipelines = own.length ? own.filter((p) => teamPipelines.includes(p)) : teamPipelines;
    // Both set and nothing in common: keep the team's, so the report reads
    // as empty for this team rather than silently showing every pipeline.
    if (own.length && !pipelines.length) pipelines = ["__none__"];
  }
  return {
    fromMonth: window.from,
    toMonth: window.to,
    pipelines,
    stages: config.stages || [],
    excludeStages: config.exclude_stages || [],
    owners: config.owners || [],
    companies: config.companies || [],
    products: config.products || [],
    billingFrequencies: config.billing_frequencies || [],
    includeOneTime: Boolean(config.include_one_time),
    collapseProducts: Boolean(config.collapse_products),
    excludeNonXero: Boolean(config.exclude_non_xero),
    probabilityMin: config.probability_min ?? undefined,
    probabilityMax: config.probability_max ?? undefined,
  };
}

/** How far MRR has moved from the first month of the year to the latest one. */
export function growthOverYear(months = [], today = new Date()) {
  if (!months.length) return null;
  const thisMonth = `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
  const actual = months.filter((m) => m.month <= thisMonth);
  const first = months[0];
  const last = actual.length ? actual[actual.length - 1] : null;
  if (!last || last.month === first.month || !first.total_mrr) return null;
  return {
    from: first,
    to: last,
    pct: ((last.total_mrr - first.total_mrr) / first.total_mrr) * 100,
    complete: last.month === months[months.length - 1].month,
  };
}

const VIEWS = [
  ["stacked", "Stacked"],
  ["grouped", "Grouped"],
  ["table", "Table"],
];

export default function RevenueReportCard({
  portalId,
  report: packEntry,
  fyStartYear,
  fyStartMonth = 7,
  currency = "GBP",
  teams = [],
  showTeam = true,
  onControlChange,
  controlsBusy = false,
  children,
}) {
  const config = packEntry?.revenue || {};
  const [view, setView] = useState(config.view || "stacked");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMonth, setDetailMonth] = useState(null);
  // A previous fiscal year drawn over this one. Revenue is the only report
  // that can answer for an earlier year, so the choice lives here rather than
  // on the dashboard.
  const [compareYear, setCompareYear] = useState(null);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    setView(config.view || "stacked");
  }, [config.view]);

  const filters = useMemo(
    () =>
      revenueFilters(
        config,
        packEntry?.team_pipelines,
        fyStartYear ? fiscalYearWindow(fyStartYear, fyStartMonth) : {},
      ),
    // The config object is rebuilt on every pack load; its content is what
    // matters, so compare it as JSON.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(config), JSON.stringify(packEntry?.team_pipelines), fyStartYear, fyStartMonth],
  );

  const load = useCallback(async () => {
    if (!portalId || !fyStartYear) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await fetchReport(portalId, filters, fyStartMonth));
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load the MRR report.");
    } finally {
      setLoading(false);
    }
  }, [portalId, filters, fyStartMonth, fyStartYear]);

  useEffect(() => {
    load();
  }, [load]);

  const compareFilters = useMemo(
    () =>
      compareYear
        ? revenueFilters(config, packEntry?.team_pipelines, fiscalYearWindow(compareYear, fyStartMonth))
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(config), JSON.stringify(packEntry?.team_pipelines), compareYear, fyStartMonth],
  );

  useEffect(() => {
    let cancelled = false;
    if (!portalId || !compareFilters) {
      setComparison(null);
      return undefined;
    }
    (async () => {
      try {
        const data = await fetchReport(portalId, compareFilters, fyStartMonth);
        if (!cancelled) setComparison(data);
      } catch {
        if (!cancelled) setComparison(null);
      }
    })();
    return () => { cancelled = true; };
  }, [portalId, compareFilters, fyStartMonth]);

  const formatCurrency = useCallback(
    (value, compact = false) =>
      new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
        notation: compact ? "compact" : "standard",
      }).format(Number(value || 0)),
    [currency],
  );
  const formatMonthLabel = useCallback((month) => {
    if (!month) return "";
    const date = new Date(`${String(month).slice(0, 7)}-01T00:00:00`);
    if (Number.isNaN(date.getTime())) return String(month);
    return date.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
  }, []);

  const openMonth = async (month) => {
    if (!portalId || !month) return;
    setDetailMonth(String(month).slice(0, 7));
    setDrilldownOpen(true);
    setDetailLoading(true);
    try {
      // The drilldown lists recurring revenue, as the Reporting page's does.
      const { includeOneTime: _ignored, ...recurring } = filters;
      setDetail(await fetchMonthDetails(portalId, String(month).slice(0, 7), recurring));
    } catch (err) {
      setDetail(null);
      setError(err?.response?.data?.detail || "Could not load that month.");
    } finally {
      setDetailLoading(false);
    }
  };

  const refetchMonth = async (changesOnly) => {
    if (!detailMonth) return;
    setDetailLoading(true);
    try {
      const { includeOneTime: _ignored, ...recurring } = filters;
      setDetail(await fetchMonthDetails(portalId, detailMonth, recurring, changesOnly));
    } finally {
      setDetailLoading(false);
    }
  };

  const growth = config.show_growth !== false ? growthOverYear(report?.months || []) : null;
  const latest = growth?.to || (report?.months || [])[0];

  return (
    <section
      className="break-inside-avoid rounded-md border border-[#cccccc] bg-white p-5"
      data-testid="report-card-mrr"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-sans text-base font-bold text-[#323c44]">
            {packEntry?.title || "Monthly recurring revenue"}
          </h3>
          {packEntry?.subtitle && <p className="mt-1 text-sm text-[#4a5b68]">{packEntry.subtitle}</p>}
          {onControlChange && (
            <div className="mt-2">
              <ReportControls
                teams={teams}
                showTeam={showTeam}
                team={packEntry?.team || "all"}
                groupProducts={packEntry?.display?.group_products}
                showGroupProducts
                onChange={(patch) => onControlChange(packEntry.key, patch)}
                busy={controlsBusy}
              />
            </div>
          )}
          {/* Revenue is the one report that can answer for an earlier year. */}
          {comparisonYears(fyStartYear).length > 0 && (
            <label className="mt-2 flex items-center gap-1.5 text-xs text-[#4a5b68] print:hidden">
              <span className="font-semibold uppercase tracking-[0.1em] text-[#7d8b96]">
                FY comparison
              </span>
              <select
                value={compareYear ?? ""}
                onChange={(event) =>
                  setCompareYear(event.target.value === "" ? null : Number(event.target.value))
                }
                className="rounded-full border border-[#cccccc] bg-white px-2.5 py-0.5 text-xs text-[#323c44] hover:border-[#7d8b96] focus:border-[#57c1a9] focus:outline-none"
                data-testid="fy-comparison-select"
              >
                <option value="">None</option>
                {comparisonYears(fyStartYear).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="flex items-center gap-3">
          {latest && (
            <div className="text-right">
              <p className="font-sans text-xl font-bold leading-none">
                <span className="inline-block rounded-full bg-[#efefef] px-3 py-1 tabular-nums text-[#323c44]">
                  {formatCurrency(latest.total_mrr)}
                </span>
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">
                MRR, {formatMonthLabel(latest.month)}
              </p>
            </div>
          )}
          <div className="flex gap-1 print:hidden">
            {VIEWS.map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setView(mode)}
                className={`rounded border px-2.5 py-1 text-xs font-semibold ${
                  view === mode
                    ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                    : "border-[#cccccc] bg-white text-[#4a5b68] hover:border-[#7d8b96]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {children}
        </div>
      </div>

      {loading && (
        <p className="flex items-center justify-center gap-2 py-16 text-sm text-[#4a5b68]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading revenue…
        </p>
      )}
      {error && (
        <p className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </p>
      )}

      {!loading && !error && report && (report.products || []).length > 0 && (
        view === "table" ? (
          <ReportTable report={report} formatCurrency={formatCurrency} formatMonthLabel={formatMonthLabel} />
        ) : (
          <ReportChart
            report={report}
            settings={{ showLegend: true, showTotals: true }}
            // Each product in its brand colour, in the chart's own order.
            palette={(report.products || []).map((product, index) => colorFor(product, index))}
            viewMode={view}
            onMonthClick={openMonth}
            formatCurrency={formatCurrency}
            formatMonthLabel={formatMonthLabel}
            comparison={comparison ? comparisonByIndex(comparison.months) : null}
            comparisonLabel={
              compareYear ? `FY ${compareYear}/${String(compareYear + 1).slice(2)}` : null
            }
          />
        )
      )}
      {comparison && report && (
        <p className="mt-2 text-xs text-[#4a5b68]" data-testid="fy-comparison-line">
          {(() => {
            const nowMonths = (report.months || []).filter((m) => Number(m.total_mrr) > 0);
            const then = comparisonByIndex(comparison.months);
            const index = Math.max(0, nowMonths.length - 1);
            const now = nowMonths[index]?.total_mrr;
            const before = then[index];
            if (!now || !before) return `FY ${compareYear}/${String(compareYear + 1).slice(2)} is drawn as the dashed line.`;
            const change = ((now - before) / before) * 100;
            return `At the same point in FY ${compareYear}/${String(compareYear + 1).slice(2)}, recurring revenue was `
              + `${formatCurrency(before)} a month, against ${formatCurrency(now)} now: `
              + `${change >= 0 ? "up" : "down"} ${Math.abs(change).toFixed(1)}%.`;
          })()}
        </p>
      )}
      {!loading && !error && report && !(report.products || []).length && (
        <p className="py-12 text-center text-sm text-[#4a5b68]">No recurring revenue matches this report.</p>
      )}

      {/* The movement over the year, as the board pack states it. */}
      {!loading && growth && (
        <div className="mt-4 flex items-center gap-3 rounded-md border border-[#efefef] bg-[#f5f5f5] px-4 py-3">
          {growth.pct >= 0 ? (
            <TrendingUp className="h-5 w-5 flex-shrink-0 text-emerald-600" />
          ) : (
            <TrendingDown className="h-5 w-5 flex-shrink-0 text-red-600" />
          )}
          <p className="text-sm text-[#323c44]">
            MRR has {growth.pct >= 0 ? "grown" : "fallen"}{" "}
            <span className="font-bold">{Math.abs(growth.pct).toFixed(1)}%</span>{" "}
            {growth.complete ? "over the year" : `since ${formatMonthLabel(growth.from.month)}`}, from{" "}
            {formatCurrency(growth.from.total_mrr)} to {formatCurrency(growth.to.total_mrr)} a month.
          </p>
        </div>
      )}

      <HowItWorks result={packEntry} />

      {!loading && !error && report && (
        <p className="mt-4 border-t border-[#efefef] pt-3 text-[11px] leading-relaxed text-[#7d8b96]">
          <span className="font-semibold uppercase tracking-[0.14em]">Source</span> the revenue reports:
          whole-month attribution, renewals from 26/27 starting on their annual review date, and RPI taken live from ONS
          {report.rpi ? ` (currently ${report.rpi}%)` : ""}.
          <span className="print:hidden"> Click a month to see the line items behind it.</span>
        </p>
      )}

      <DrilldownSheet
        open={drilldownOpen}
        onOpenChange={setDrilldownOpen}
        detail={detail}
        loading={detailLoading}
        formatCurrency={formatCurrency}
        formatMonthLabel={formatMonthLabel}
        onRefetch={refetchMonth}
      />
    </section>
  );
}
